import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/lib/api/users.api';
import { profileImageCache } from '@/lib/services/profile-image-cache.service';
import { queryKeys } from '../query-keys';
import { useAuth } from '@/store/auth.store';
import type { User } from '@scrambl/shared';

/**
 * Hook to fetch the current user's profile
 */
export function useCurrentUserProfileQuery(options?: { enabled?: boolean }) {
  const { isAuthenticated } = useAuth();

  return useQuery<User>({
    queryKey: queryKeys.userProfile.currentUser(),
    queryFn: async () => {
      return usersApi.getProfile();
    },
    enabled: (options?.enabled !== false && isAuthenticated) ?? false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch a user's profile image
 * This uses the centralized profile image cache service
 */
export function useUserProfileImageQuery(
  userId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<string | null>({
    queryKey: queryKeys.userProfile.profileImage(userId!),
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      return profileImageCache.getProfileImage(userId);
    },
    enabled: (options?.enabled !== false && !!userId) ?? false,
    staleTime: 30 * 60 * 1000, // 30 minutes - profile images don't change often
  });
}

